import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { AssigTripsService } from 'src/app/component/assigned-trips/assigntrips-service/assigntrips-service';
import { PlannerHelpingService } from './planner-helping-service';

/**
 * World-Class VRP Engine for Leg-Lock (Same-Legs) Planning:
 *
 * In NEMT operations, a client often has multiple legs on the same service date
 * (e.g. Leg A: Home -> Clinic in the morning; Leg B: Clinic -> Home in the afternoon).
 *
 * The Leg-Lock rule mandates that ALL legs belonging to the same client MUST be
 * locked to the SAME driver.
 *
 * This engine implements:
 * 1. Client Leg Bundling: Groups trips by client, sorting legs chronologically.
 * 2. Multi-Leg Bundle Eligibility: Ensures the driver satisfies shift hours, funding sources,
 *    service areas, vehicle capacities, and constraints across ALL legs in the bundle.
 * 3. Precedence & Temporal Feasibility: Guarantees Leg 1 dropoff happens before Leg 2 pickup,
 *    with road-network travel times, dwell times, and driver shift checkout boundaries.
 * 4. Regret-2 Insertion Heuristic: Prioritizes multi-leg client bundles with tight driver choices
 *    first, eliminating stranded afternoon return legs.
 * 5. Full Chronological Stop Generation: Emits discrete pickup/dropoff stops with ETAs,
 *    `pickupOrder`, `dropOffOrder`, and visual labels (`PU #1 → DO #2`).
 * 6. Local Search & Rescue: Intra-route 2-opt and non-compatible bundle rescue.
 */
@Injectable({
  providedIn: 'root'
})
export class PlannerSameLegsService {

  user: any;
  preData: any;
  preFleet: any;
  selectedMapper: any;
  violation: any;
  selectedDrivers: any[];
  selectedMethod: string;
  timeCalibration: any;
  shiftMapper: any;
  preDrivers: any;

  constructor(
    private assigTripsService: AssigTripsService,
    private plannerHelpingService: PlannerHelpingService
  ) {}

  async planTrips(
    user: any,
    preData: any,
    preFleet: any,
    violation: any,
    selectedMapper: any,
    selectedDrivers: any[],
    selectedMethod: string,
    timeCalibration: any,
    shiftMapper: any,
    manifestTrips: any[] = [],
    preDrivers: any = {}
  ): Promise<any> {
    this.user = user;
    this.preData = preData;
    this.preFleet = preFleet;
    this.violation = violation;
    this.selectedMapper = selectedMapper;
    this.selectedDrivers = selectedDrivers;
    this.selectedMethod = selectedMethod;
    this.timeCalibration = timeCalibration;
    this.shiftMapper = shiftMapper;
    this.preDrivers = preDrivers;

    let planedTrips: any = {
      trips: [],
      nonCompatible: [],
      compatible: {},
      tripLogs: {},
      driversDistance: {}
    };

    const incomingTrips: any[] = [];
    const selectedTripSet = new Set<string>();
    if (this.preData.selectedTrips && Array.isArray(this.preData.selectedTrips)) {
      this.preData.selectedTrips.forEach(id => selectedTripSet.add(String(id)));
    }
    for (let trip of this.preData.trips) {
      if (selectedTripSet.has(String(trip._id))) {
        incomingTrips.push(trip);
      }
    }

    // Apply Time Calibration
    if (this.timeCalibration && this.timeCalibration !== "none") {
      if (this.timeCalibration === "pickup-wise") {
        await this.plannerHelpingService.applyPickupWiseCalibration(this.selectedMethod, incomingTrips, user);
      } else if (this.timeCalibration === "dropoff-wise") {
        await this.plannerHelpingService.applyDropoffWiseCalibration(this.selectedMethod, incomingTrips, user);
      } else {
        await this.plannerHelpingService.applyMemberWiseHybridCalibration(this.selectedMethod, incomingTrips, user);
      }
    }

    this.selectedDrivers.sort((a, b) =>
      (a.displayName || '').localeCompare(b.displayName || '')
    );

    // Populate pre-manifest trips if any
    if (manifestTrips && manifestTrips.length) {
      for (let item of manifestTrips) {
        if (this.selectedDrivers.some(driver => driver._id === item.driver)) {
          if (item.driver) {
            if (!planedTrips.compatible[item.driver]) {
              planedTrips.compatible[item.driver] = {
                driver: preDrivers[item.driver],
                trips: []
              };
            }
            if (item) {
              item.preTrip = true;
              planedTrips.compatible[item.driver].trips.push(item);
            }
          }
        }
      }
      for (const driverId in planedTrips.compatible) {
        const driverData = planedTrips.compatible[driverId];
        if (driverData.trips && driverData.trips.length > 0 && !driverData.executedStops) {
          await this.initializeExecutedStopsForTrips(driverData.driver, driverData);
        }
      }
    }

    // Run Leg-Lock Regret-2 VRP Engine
    await this.assignTripsToDrivers(planedTrips, incomingTrips, this.selectedDrivers);
    await this.finalizeSequences(planedTrips);

    // reset trips array
    planedTrips.trips = [];

    // Final Log summary
    for (const driverId in planedTrips.compatible) {
      const data = planedTrips.compatible[driverId];
      if (data.driver) {
        console.log(`[Leg-Lock VRP] Driver: ${data.driver.displayName} (${data.trips.length} trips, ${(planedTrips.driversDistance[driverId] || 0).toFixed(1)} miles)`);
        console.log(`[Leg-Lock VRP] Trips IDs: ${data.trips.map(t => t.tripId).join(', ')}`);
        if (data.stops) {
          console.log(`[Leg-Lock VRP] Stops:`, data.stops.map(s => `${s.priority}. [${s.stopType}] Patient: ${s.personName || 'Unknown'} (${s.tripId}) ETA: ${s.estimatedArrivalTime}`));
        }
      }
    }

    return planedTrips;
  }

  /**
   * Initializes executed stops and baseline metrics for pre-assigned manifest trips.
   */
  async initializeExecutedStopsForTrips(driver: any, driverData: any) {
    const sim = await this.simulateRouteFeasibility(driver, driverData.trips);
    if (sim.isFeasible) {
      driverData.executedStops = sim.stops;
    }
  }

  /**
   * Identifies the unique client key for leg bundling.
   */
  private getClientKey(trip: any): string {
    if (trip.client) return String(trip.client);
    if (trip.priorityClient && trip.priorityClient.client) return String(trip.priorityClient.client);
    if (trip.priorityClient && trip.priorityClient.displayName) return trip.priorityClient.displayName.trim().toLowerCase();
    return String(trip._id);
  }

  /**
   * Bundle incoming trips by client.
   */
  private buildClientBundles(trips: any[]): any[] {
    const bundleMap: { [key: string]: any } = {};

    for (const trip of trips) {
      const key = this.getClientKey(trip);
      if (!bundleMap[key]) {
        bundleMap[key] = {
          clientId: key,
          clientName: (trip.priorityClient && trip.priorityClient.displayName) ? trip.priorityClient.displayName : key,
          trips: []
        };
      }
      bundleMap[key].trips.push(trip);
    }

    const bundles: any[] = [];
    for (const key in bundleMap) {
      const bundle = bundleMap[key];
      // Sort trips within the bundle chronologically (Leg 1 before Leg 2)
      bundle.trips.sort((a, b) => {
        const aTime = a.scheduleTime ? new Date(a.scheduleTime).getTime() : 0;
        const bTime = b.scheduleTime ? new Date(b.scheduleTime).getTime() : 0;
        return aTime - bTime;
      });
      bundles.push(bundle);
    }

    return bundles;
  }

  /**
   * Core Regret-2 Insertion Engine for Leg-Lock VRP:
   * 1. Evaluates all client bundles across all candidate drivers.
   * 2. All legs of a bundle MUST be feasible on the same driver.
   * 3. Calculates regret = Cost(2nd best driver) - Cost(1st best driver).
   * 4. Multi-leg bundles and bottleneck clients are scheduled first.
   */
  async assignTripsToDrivers(planedTrips: any, incomingTrips: any[], drivers: any[]) {
    const unassignedBundles = this.buildClientBundles(incomingTrips);

    while (unassignedBundles.length > 0) {
      let bestCandidate: any = null;
      let highestRegret = -Infinity;
      let bestBundleIndex = -1;

      // Evaluate each unassigned bundle
      for (let bIdx = 0; bIdx < unassignedBundles.length; bIdx++) {
        const bundle = unassignedBundles[bIdx];

        // Filter drivers who are eligible for ALL legs in the bundle
        const candidateDrivers = drivers.filter(driver => {
          return bundle.trips.every(trip => this.isTripEligibleForDriver(planedTrips, trip, driver));
        });

        if (candidateDrivers.length === 0) {
          continue;
        }

        // Evaluate feasible insertions across candidate drivers
        const driverEvaluations: any[] = [];
        for (const driver of candidateDrivers) {
          const evalResult = await this.evaluateBundleInsertion(planedTrips, driver, bundle);
          if (evalResult && evalResult.isFeasible) {
            driverEvaluations.push({
              driver,
              ...evalResult
            });
          }
        }

        if (driverEvaluations.length === 0) {
          continue;
        }

        // Sort by cost score ascending (lowest cost first)
        driverEvaluations.sort((a, b) => a.score - b.score);

        const bestOption = driverEvaluations[0];
        let regret = 0;
        if (driverEvaluations.length === 1) {
          // Bottleneck: Only 1 driver in the entire fleet can do this bundle!
          regret = 10000 - bestOption.score;
        } else {
          // Regret: opportunity cost of losing the best driver
          regret = driverEvaluations[1].score - bestOption.score;
        }

        // Bonus urgency for multi-leg bundles (securing return legs before drivers fill up)
        if (bundle.trips.length > 1) {
          regret += 200 * (bundle.trips.length - 1);
        }

        if (regret > highestRegret) {
          highestRegret = regret;
          bestCandidate = {
            bundle,
            bundleIndex: bIdx,
            driverOption: bestOption
          };
        }
      }

      if (!bestCandidate) {
        // No remaining bundles can be feasibly assigned
        break;
      }

      // Assign bundle to best driver
      const { bundle, bundleIndex, driverOption } = bestCandidate;
      const driverId = driverOption.driver._id;

      if (!planedTrips.compatible[driverId]) {
        planedTrips.compatible[driverId] = {
          driver: driverOption.driver,
          trips: [],
          executedStops: []
        };
      }

      const driverAssignment = planedTrips.compatible[driverId];
      driverAssignment.trips = driverOption.orderedTrips;
      driverAssignment.executedStops = driverOption.stops;
      planedTrips.driversDistance[driverId] = driverOption.totalDistance;

      // Remove from unassigned
      unassignedBundles.splice(bundleIndex, 1);
    }

    // Move unassigned trips to nonCompatible
    for (const bundle of unassignedBundles) {
      planedTrips.nonCompatible.push(...bundle.trips);
      console.warn(`[Leg-Lock VRP] Client ${bundle.clientName} (${bundle.trips.length} legs) could not be assigned to a single driver.`);
    }

    // Run Local Search (Intra-route 2-Opt)
    await this.optimizeRoutesLocalSearch(planedTrips, drivers);

    // Run Rescue Pass for non-compatible trips
    if (planedTrips.nonCompatible.length > 0) {
      await this.rescueNonCompatibleBundles(planedTrips, drivers);
    }
  }

  /**
   * Evaluates inserting an entire client bundle into a driver's route.
   */
  async evaluateBundleInsertion(planedTrips: any, driver: any, bundle: any): Promise<any> {
    const assignedTrips: any[] = (planedTrips.compatible[driver._id] && planedTrips.compatible[driver._id].trips)
      ? planedTrips.compatible[driver._id].trips
      : [];

    // Combine existing trips with bundle trips
    const combinedTrips = [...assignedTrips, ...bundle.trips];

    // Sort chronologically by scheduled pickup time
    combinedTrips.sort((a, b) => {
      const aTime = a.scheduleTime ? new Date(a.scheduleTime).getTime() : 0;
      const bTime = b.scheduleTime ? new Date(b.scheduleTime).getTime() : 0;
      return aTime - bTime;
    });

    // Check precedence: Leg 1 must end before Leg 2 starts
    for (let i = 0; i < bundle.trips.length - 1; i++) {
      const legA = bundle.trips[i];
      const legB = bundle.trips[i + 1];
      const legAAppt = moment(legA.appointmentTime).tz(this.user.timeZone).valueOf();
      const legBSched = moment(legB.scheduleTime).tz(this.user.timeZone).valueOf();
      if (legAAppt > legBSched) {
        return { isFeasible: false, reason: 'Leg B pickup is before Leg A appointment' };
      }
    }

    // Check capacity / maxPerRun constraints
    if (this.violation && this.violation.maxPerRun) {
      if (combinedTrips.length > this.violation.maxPerRun) {
        return { isFeasible: false, reason: 'Exceeds max trips per run' };
      }
    }

    // Simulate route feasibility
    const sim = await this.simulateRouteFeasibility(driver, combinedTrips);
    if (!sim.isFeasible) {
      return { isFeasible: false, reason: sim.reason };
    }

    if (this.violation && this.violation.maxDistancePerRun) {
      if (sim.totalDistance > this.violation.maxDistancePerRun) {
        return { isFeasible: false, reason: 'Exceeds max distance per run' };
      }
    }

    // Spatio-Temporal Cost Function:
    // Balances deadhead miles, transit duration, driver workload, and excessive idle waiting
    let score = sim.deadheadDistance + (0.15 * sim.totalDuration) + (0.3 * assignedTrips.length);

    if (sim.idleMinutes && sim.idleMinutes > 30) {
      score += 0.05 * (sim.idleMinutes - 30);
    }

    // Preferred driver discount
    const isPreferred = bundle.trips.some(t => {
      return (t.preferredDriver && t.preferredDriver === driver._id) ||
        (t.priorityClient && t.priorityClient.preferredDriver === driver._id);
    });
    if (isPreferred) {
      score -= 30;
    }

    return {
      isFeasible: true,
      orderedTrips: combinedTrips,
      stops: sim.stops,
      totalDistance: sim.totalDistance,
      totalDuration: sim.totalDuration,
      deadheadDistance: sim.deadheadDistance,
      score: score
    };
  }

  /**
   * Chronological Route Simulation Engine:
   * Validates driver shift boundaries, break cushions, dwell times, and strict dropoff on-time compliance.
   */
  async simulateRouteFeasibility(driver: any, trips: any[]): Promise<any> {
    if (!trips || trips.length === 0) {
      return { isFeasible: false, reason: 'Empty trip list' };
    }

    const startOfDay = moment(this.preData.date).tz(this.user.timeZone).startOf('day');
    const todayShift = this.shiftMapper && this.shiftMapper[driver._id] && this.shiftMapper[driver._id].shifts
      ? this.shiftMapper[driver._id].shifts.find(s => s.day === startOfDay.day() && s.isEnabled)
      : null;

    if (!todayShift || !todayShift.checkin || !todayShift.checkout) {
      return { isFeasible: false, reason: 'Driver shift not found' };
    }

    let currentTime = moment(startOfDay).set({
      hour: Number(todayShift.checkin.split(':')[0]),
      minute: Number(todayShift.checkin.split(':')[1])
    });

    let checkoutTime = moment(startOfDay).set({
      hour: Number(todayShift.checkout.split(':')[0]),
      minute: Number(todayShift.checkout.split(':')[1])
    });

    let currentLat = driver.startingPointAddress ? driver.startingPointAddress.latitude : null;
    let currentLon = driver.startingPointAddress ? driver.startingPointAddress.longitude : null;

    if (currentLat === null || currentLon === null) {
      return { isFeasible: false, reason: 'Driver has no starting point coordinates' };
    }

    const relaxationMargin = (this.violation && this.violation.groupCushion) ? this.violation.groupCushion : 15;
    const breakCushion = (this.violation && this.violation.breakCushion) ? this.violation.breakCushion : 5;

    let totalDistance = 0;
    let totalDuration = 0;
    let totalDeadheadDistance = 0;
    let totalIdleMinutes = 0;
    const executedStops: any[] = [];

    for (let i = 0; i < trips.length; i++) {
      const trip = trips[i];
      const stopPair = this.buildStopsForTrip(trip);
      const pStop = stopPair[0];
      const dStop = stopPair[1];

      // Break cushion between consecutive trips
      if (i > 0) {
        currentTime.add(breakCushion, 'minutes');
      }

      // Deadhead from previous location to pickup
      const deadhead = await this.calculateDistance(currentLat, currentLon, pStop.lat, pStop.lon);
      const deadheadDist = deadhead && deadhead.distance ? deadhead.distance : 0;
      const deadheadDur = deadhead && deadhead.duration ? deadhead.duration : 0;

      totalDistance += deadheadDist;
      totalDuration += deadheadDur;
      totalDeadheadDistance += deadheadDist;
      currentTime.add(deadheadDur, 'minutes');

      // Pickup Time Window check
      const pTargetTime = moment(pStop.time).tz(this.user.timeZone);
      const earliestWindow = pTargetTime.clone().subtract(15, 'minutes');

      const idleTime = pTargetTime.diff(currentTime, 'minutes');
      if (idleTime > 20) {
        totalIdleMinutes += idleTime;
        currentTime = pTargetTime.clone();
      } else if (currentTime.isBefore(earliestWindow)) {
        totalIdleMinutes += earliestWindow.diff(currentTime, 'minutes');
        currentTime = earliestWindow.clone();
      }

      pStop.estimatedArrivalTime = currentTime.toISOString();

      if (this.violation && !this.violation.isTimeViolation) {
        if (currentTime.isAfter(pTargetTime.clone().add(relaxationMargin, 'minutes'))) {
          return { isFeasible: false, reason: `Pickup arrival exceeded relaxation margin for ${trip.tripId}` };
        }
      }

      // Load Service Time
      let loadMinutes = 3;
      const stData = this.preData.spaceTypes ? this.preData.spaceTypes[pStop.spaceType] : null;
      if (stData && stData.loadTime && stData.loadTime.includes(':')) {
        const parts = stData.loadTime.split(':').map(Number);
        loadMinutes = (parts[0] * 60) + parts[1];
      }
      currentTime.add(loadMinutes, 'minutes');

      if (currentTime.isBefore(pTargetTime)) {
        currentTime = pTargetTime.clone();
      }

      // Trip Leg: Direct Pickup -> Dropoff
      const tripLeg = await this.calculateDistance(pStop.lat, pStop.lon, dStop.lat, dStop.lon);
      const tripDist = tripLeg && tripLeg.distance ? tripLeg.distance : 0;
      const tripDur = tripLeg && tripLeg.duration ? tripLeg.duration : 0;

      totalDistance += tripDist;
      totalDuration += tripDur;
      currentTime.add(tripDur, 'minutes');

      dStop.estimatedArrivalTime = currentTime.toISOString();

      // Strict Dropoff Appointment Compliance (ETA <= appointmentTime + 5 min buffer)
      const dTargetTime = moment(dStop.time).tz(this.user.timeZone);
      const appointmentBuffer = 5;
      const maxAllowedArrival = dTargetTime.clone().add(appointmentBuffer, 'minutes');

      if (currentTime.isAfter(maxAllowedArrival)) {
        return {
          isFeasible: false,
          reason: `Late dropoff for ${trip.tripId}: arrives ${currentTime.format('HH:mm')}, appointment was ${dTargetTime.format('HH:mm')}`
        };
      }

      // Unload Service Time
      let unloadMinutes = 3;
      if (stData && stData.unloadTime && stData.unloadTime.includes(':')) {
        const parts = stData.unloadTime.split(':').map(Number);
        unloadMinutes = (parts[0] * 60) + parts[1];
      }
      currentTime.add(unloadMinutes, 'minutes');

      currentLat = dStop.lat;
      currentLon = dStop.lon;
      executedStops.push(pStop, dStop);
    }

    // Driver Shift Checkout limit check
    if (currentTime.isAfter(checkoutTime.clone().add(relaxationMargin, 'minutes'))) {
      return { isFeasible: false, reason: `Route exceeds driver shift checkout (${currentTime.format('HH:mm')} > ${checkoutTime.format('HH:mm')})` };
    }

    return {
      isFeasible: true,
      stops: executedStops.map((s, idx) => ({ ...s, priority: idx + 1 })),
      totalDistance: Math.round(totalDistance * 10) / 10,
      totalDuration: Math.round(totalDuration),
      deadheadDistance: Math.round(totalDeadheadDistance * 10) / 10,
      idleMinutes: totalIdleMinutes
    };
  }

  /**
   * Builds the [Pickup, Dropoff] stop pair for a given trip.
   */
  private buildStopsForTrip(trip: any): any[] {
    const contact = (trip.priorityClient && trip.priorityClient.contactNumber) ? trip.priorityClient.contactNumber : '';
    const pickupStop = {
      _id: trip._id,
      tripId: trip.tripId,
      type: 'P',
      time: moment(trip.scheduleTime).tz(this.user.timeZone).valueOf(),
      lat: trip.jobOriginLatitude,
      lon: trip.jobOriginLongitude,
      address: trip.jobOriginAddress || '',
      contactNumber: contact,
      spaceType: trip.spaceType,
      priorityClient: trip.priorityClient,
      scheduleTime: trip.scheduleTime,
      appointmentTime: trip.appointmentTime,
      pickupLat: trip.jobOriginLatitude,
      pickupLon: trip.jobOriginLongitude,
      dropoffLat: trip.jobDestinationLatitude,
      dropoffLon: trip.jobDestinationLongitude
    };

    const dropoffStop = {
      _id: trip._id,
      tripId: trip.tripId,
      type: 'D',
      time: moment(trip.appointmentTime).tz(this.user.timeZone).valueOf(),
      lat: trip.jobDestinationLatitude,
      lon: trip.jobDestinationLongitude,
      address: trip.jobDestinationAddress || '',
      contactNumber: contact,
      spaceType: trip.spaceType,
      priorityClient: trip.priorityClient,
      scheduleTime: trip.scheduleTime,
      appointmentTime: trip.appointmentTime,
      pickupLat: trip.jobOriginLatitude,
      pickupLon: trip.jobOriginLongitude,
      dropoffLat: trip.jobDestinationLatitude,
      dropoffLon: trip.jobDestinationLongitude
    };

    return [pickupStop, dropoffStop];
  }

  /**
   * Post-Optimization Local Search (Intra-route 2-Opt):
   * Tests swapping adjacent non-bundle-constrained trips if it reduces deadhead distance.
   */
  async optimizeRoutesLocalSearch(planedTrips: any, drivers: any[]) {
    const driverIds = Object.keys(planedTrips.compatible);

    for (const driverId of driverIds) {
      const driverData = planedTrips.compatible[driverId];
      if (!driverData.trips || driverData.trips.length <= 1) continue;

      let improved = true;
      let iteration = 0;
      while (improved && iteration < 3) {
        improved = false;
        iteration++;

        for (let i = 0; i < driverData.trips.length - 1; i++) {
          const tripA = driverData.trips[i];
          const tripB = driverData.trips[i + 1];

          // If tripA and tripB belong to the same client, preserve chronological leg order
          if (this.getClientKey(tripA) === this.getClientKey(tripB)) {
            continue;
          }

          const candidateTrips = [...driverData.trips];
          candidateTrips[i] = tripB;
          candidateTrips[i + 1] = tripA;

          const sim = await this.simulateRouteFeasibility(driverData.driver, candidateTrips);
          if (sim.isFeasible && sim.totalDistance < planedTrips.driversDistance[driverId] - 0.2) {
            driverData.trips = candidateTrips;
            driverData.executedStops = sim.stops;
            planedTrips.driversDistance[driverId] = sim.totalDistance;
            improved = true;
            break;
          }
        }
      }
    }
  }

  /**
   * Rescue pass for client bundles that could not be scheduled in the first pass.
   */
  async rescueNonCompatibleBundles(planedTrips: any, drivers: any[]) {
    const unassignedTrips = [...planedTrips.nonCompatible];
    const bundles = this.buildClientBundles(unassignedTrips);

    for (let bIdx = bundles.length - 1; bIdx >= 0; bIdx--) {
      const bundle = bundles[bIdx];
      let bestChoice: any = null;
      let bestScore = Infinity;

      for (const driver of drivers) {
        const isEligible = bundle.trips.every(trip => this.isTripEligibleForDriver(planedTrips, trip, driver));
        if (!isEligible) continue;

        const evalResult = await this.evaluateBundleInsertion(planedTrips, driver, bundle);
        if (evalResult && evalResult.isFeasible && evalResult.score < bestScore) {
          bestScore = evalResult.score;
          bestChoice = { driver, ...evalResult };
        }
      }

      if (bestChoice) {
        const driverId = bestChoice.driver._id;
        if (!planedTrips.compatible[driverId]) {
          planedTrips.compatible[driverId] = {
            driver: bestChoice.driver,
            trips: [],
            executedStops: []
          };
        }

        const driverAssignment = planedTrips.compatible[driverId];
        driverAssignment.trips = bestChoice.orderedTrips;
        driverAssignment.executedStops = bestChoice.stops;
        planedTrips.driversDistance[driverId] = bestChoice.totalDistance;

        // Remove from nonCompatible
        bundle.trips.forEach(t => {
          const idx = planedTrips.nonCompatible.findIndex(nc => nc._id === t._id);
          if (idx >= 0) {
            planedTrips.nonCompatible.splice(idx, 1);
          }
        });

        console.log(`[Leg-Lock VRP] Rescued Client ${bundle.clientName} (${bundle.trips.length} legs) into Driver ${bestChoice.driver.displayName}!`);
      }
    }
  }

  /**
   * Formats executed stops into user-facing stops array and attaches pickup/dropoff orders.
   */
  async finalizeSequences(planedTrips: any) {
    for (const driverId in planedTrips.compatible) {
      const driverData = planedTrips.compatible[driverId];
      if (driverData.executedStops) {
        driverData.stops = driverData.executedStops.map((stop, index) => {
          const stopObj = {
            ...stop,
            priority: index + 1,
            latitude: stop.lat,
            longitude: stop.lon,
            address: stop.address || '',
            contactNumber: stop.contactNumber || '',
            passengersOnBoard: 1,
            personName: stop.priorityClient ? stop.priorityClient.displayName : '',
            sortType: stop.type === 'P' ? 'pickup' : 'dropoff',
            stopType: stop.type === 'P' ? 'pickup' : 'dropoff',
            scheduledTime: stop.scheduleTime,
            appointmentTime: stop.appointmentTime,
            estimatedArrivalTime: stop.estimatedArrivalTime,
            isGrouped: false,
            isLegLocked: true,
            tripId: stop.tripId,
            _id: stop._id
          };
          return stopObj;
        });

        driverData.trips.forEach(trip => {
          const pickupStop = driverData.stops.find(stop => stop._id === trip._id && stop.stopType === 'pickup');
          const dropoffStop = driverData.stops.find(stop => stop._id === trip._id && stop.stopType === 'dropoff');
          trip.pickupOrder = pickupStop ? pickupStop.priority : null;
          trip.dropOffOrder = dropoffStop ? dropoffStop.priority : null;
          trip.priority = trip.pickupOrder;
          trip.isGrouped = false;
          trip.isLegLocked = true;
          trip.routeOrderLabel = (trip.pickupOrder && trip.dropOffOrder)
            ? `PU #${trip.pickupOrder} → DO #${trip.dropOffOrder}`
            : '';
        });

        this.validateAndLogAppointmentTimes(driverData, driverId);
      }
    }
  }

  /**
   * Validates appointment time compliance and logs audit metrics.
   */
  private validateAndLogAppointmentTimes(driverData: any, driverId: string) {
    try {
      const appointmentBuffer = 5;
      const appointmentMetrics = {
        total: 0,
        onTime: 0,
        late: 0,
        tooEarly: 0
      };

      if (!driverData.stops) return;

      driverData.stops.forEach((stop) => {
        if (stop.stopType === 'dropoff') {
          appointmentMetrics.total++;

          const appointmentTime = moment(stop.appointmentTime).tz(this.user.timeZone);
          const estimatedArrival = moment(stop.estimatedArrivalTime).tz(this.user.timeZone);

          const minutesEarly = appointmentTime.diff(estimatedArrival, 'minutes');
          const minutesLate = estimatedArrival.diff(appointmentTime, 'minutes');

          if (minutesEarly >= 0 && minutesEarly <= appointmentBuffer) {
            appointmentMetrics.onTime++;
            console.log(
              `✓ ON-TIME [Leg-Lock]: ${stop.personName} - Appointment ${appointmentTime.format('HH:mm')}, ` +
              `Arriving ${minutesEarly}min early (within buffer)`
            );
          } else if (minutesEarly > appointmentBuffer) {
            appointmentMetrics.tooEarly++;
          } else if (minutesLate > 0) {
            appointmentMetrics.late++;
            console.error(
              `✗ LATE [Leg-Lock]: ${stop.personName} - Appointment ${appointmentTime.format('HH:mm')}, ` +
              `Arriving ${minutesLate}min LATE`
            );
          }
        }
      });

      const onTimePercentage = appointmentMetrics.total > 0
        ? ((appointmentMetrics.onTime / appointmentMetrics.total) * 100).toFixed(1)
        : 0;

      console.log(
        `\n📊 [Leg-Lock VRP] APPOINTMENT SUMMARY - Driver: ${driverData.driver ? driverData.driver.displayName : driverId}\n` +
        `  Total Appointments: ${appointmentMetrics.total}\n` +
        `  ✓ On-Time: ${appointmentMetrics.onTime} (${onTimePercentage}%)\n` +
        `  ✗ Late: ${appointmentMetrics.late}\n`
      );

    } catch (error) {
      console.error('Error validating appointment times:', error);
    }
  }

  /**
   * Checks static driver eligibility (shift, funding source, service area, space type, gender).
   */
  isTripEligibleForDriver(planedTrips: any, selectedTrip: any, driver: any): boolean {
    let isCompatible = false;
    let logs: any = {};

    const isFundingSourceWiseExist = this.fundingSourcesWiseTrip(selectedTrip, driver);
    logs.isFundingSourceWiseExist = isFundingSourceWiseExist;

    if (isFundingSourceWiseExist) {
      const isDriverShiftWiseExist = this.checkDriverShift(selectedTrip, driver);
      logs.isDriverShiftWiseExist = isDriverShiftWiseExist;

      if (isDriverShiftWiseExist) {
        const isServiceAreasWiseExist = this.serviceAreasWiseTrip(selectedTrip, driver);
        logs.isServiceAreasWiseExist = isServiceAreasWiseExist;

        if (isServiceAreasWiseExist) {
          const isSpaceTypeWiseExist = this.spaceTypeWiseTrip(selectedTrip, driver);
          logs.isSpaceTypeWiseExist = isSpaceTypeWiseExist;

          if (isSpaceTypeWiseExist) {
            const isGenderWiseExist = this.genderWiseTrip(selectedTrip, driver);
            logs.isGenderWiseExist = isGenderWiseExist;

            if (isGenderWiseExist) {
              if (!planedTrips.driversDistance[driver._id]) planedTrips.driversDistance[driver._id] = 0;
              const isMaxFactor = this.checkTripMaxFactor(planedTrips, driver, planedTrips.driversDistance[driver._id]);
              logs.isMaxFactor = isMaxFactor;

              if (isMaxFactor) {
                isCompatible = true;
              }
            }
          }
        }
      }
    }

    if (!isCompatible) {
      if (planedTrips.tripLogs[driver._id]) {
        planedTrips.tripLogs[driver._id].reasons.push({ trip: selectedTrip, logs });
      } else {
        planedTrips.tripLogs[driver._id] = { driver, reasons: [{ trip: selectedTrip, logs }] };
      }
    }

    return isCompatible;
  }

  checkDriverShift(selectedTrip: any, driver: any): boolean {
    let isExist = false;
    const start = moment(this.preData.date).tz(this.user.timeZone).startOf('day');
    const end = moment(this.preData.date).tz(this.user.timeZone).endOf('day');

    if (this.shiftMapper[driver._id] && this.shiftMapper[driver._id].shifts && this.shiftMapper[driver._id].shifts.length) {
      const checkTodayShift = this.shiftMapper[driver._id].shifts.find(item => item.day == start.day() && item.isEnabled);
      if (checkTodayShift && checkTodayShift.checkin && checkTodayShift.checkout) {
        start.set({ hour: Number(checkTodayShift.checkin.split(':')[0]), minute: Number(checkTodayShift.checkin.split(':')[1]) });
        end.set({ hour: Number(checkTodayShift.checkout.split(':')[0]), minute: Number(checkTodayShift.checkout.split(':')[1]) });

        const tripStart = moment(selectedTrip.scheduleTime).tz(this.user.timeZone);
        const tripEnd = moment(selectedTrip.appointmentTime).tz(this.user.timeZone);

        if (tripStart.isBetween(start, end, null, '[]') && tripEnd.isBetween(start, end, null, '[]')) {
          isExist = true;
        }
      }
    }
    return isExist;
  }

  checkTripMaxFactor(planedTrips: any, driver: any, totalDistance: number): boolean {
    let isExecute = true;
    if (this.violation) {
      let numberOfTrips = true;
      if (this.violation.maxPerRun) {
        if (planedTrips.compatible[driver._id] && planedTrips.compatible[driver._id].trips) {
          if (planedTrips.compatible[driver._id].trips.length >= this.violation.maxPerRun) {
            numberOfTrips = false;
          }
        }
      }

      let numberOfTripsMiles = true;
      if (this.violation.maxDistancePerRun) {
        if (totalDistance >= this.violation.maxDistancePerRun) {
          numberOfTripsMiles = false;
        }
      }

      isExecute = (numberOfTrips && numberOfTripsMiles);
    }
    return isExecute;
  }

  fundingSourcesWiseTrip(selectedTrip: any, driver: any): boolean {
    let isExist = false;
    const isfundingSource = driver.fundingSources && driver.fundingSources.some(d =>
      d.name === selectedTrip.cooperate &&
      moment(this.preData.date).isBetween(
        moment(d.validityRange[0]).tz(this.user.timeZone).subtract(1, 'day'),
        moment(d.validityRange[1]).tz(this.user.timeZone).add(1, 'day')
      )
    );

    if (selectedTrip.cooperate && isfundingSource) {
      const driverFleetId = this.selectedMapper ? this.selectedMapper.mappers[driver._id] : null;
      if (driverFleetId && this.preFleet[driverFleetId] && this.preFleet[driverFleetId].fundingSources) {
        if (this.preFleet[driverFleetId].fundingSources.includes(selectedTrip.cooperate)) {
          isExist = true;
        }
      }
    }
    return isExist;
  }

  serviceAreasWiseTrip(selectedTrip: any, driver: any): boolean {
    let isExist = false;
    if (selectedTrip.serviceArea && this.shiftMapper[driver._id] && this.shiftMapper[driver._id].serviceAreas) {
      if (this.shiftMapper[driver._id].serviceAreas.includes(selectedTrip.serviceArea)) {
        isExist = true;
      }
    }
    return isExist;
  }

  spaceTypeWiseTrip(selectedTrip: any, driver: any): boolean {
    let isExist = false;
    if (this.violation && !this.violation.isCapacityViolation) {
      const driverFleetId = this.selectedMapper ? this.selectedMapper.mappers[driver._id] : null;
      if (driverFleetId && selectedTrip.spaceType && this.preFleet[driverFleetId] && this.preFleet[driverFleetId].capacities) {
        isExist = this.preFleet[driverFleetId].capacities.some(capacity => capacity.spaceType === selectedTrip.spaceType);
      }
    } else {
      isExist = true;
    }
    return isExist;
  }

  genderWiseTrip(selectedTrip: any, driver: any): boolean {
    let isExist = true;
    if (selectedTrip.priorityClient && selectedTrip.priorityClient.isPrefferedFemale) {
      isExist = driver.gender && driver.gender.toLowerCase() === 'female';
    }
    if (selectedTrip.preferredDriver) {
      isExist = driver._id === selectedTrip.preferredDriver;
    } else if (selectedTrip.priorityClient && selectedTrip.priorityClient.preferredDriver) {
      isExist = driver._id === selectedTrip.priorityClient.preferredDriver;
    }
    return isExist;
  }

  async calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): Promise<any> {
    return await this.assigTripsService.getDistance(lat1, lon1, lat2, lon2, this.selectedMethod);
  }
}
